import { MAX_IMAGE_SIZE } from "../constants/commonStrings.constant"
import ExifReader from "exifreader"

export const svgToDataURL = (svgString: string) => {
  // const encoded = encodeURIComponent(svgString)
  //                   .replace(/'/g, '%27')
  //                   .replace(/"/g, '%22');
  const encoded = svgString
    .replace("<svg", ~svgString.indexOf("xmlns") ? "<svg" : '<svg xmlns="http://www.w3.org/2000/svg"')

    //
    //   Encode (may need a few extra replacements)
    //
    .replace(/"/g, "'")
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/{/g, "%7B")
    .replace(/}/g, "%7D")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")

    .replace(/\s+/g, " ")

  return `data:image/svg+xml,${encoded}`
}

export const changeSvgColor = (svgString: string, newColor: string) => {
  // Parse the SVG string
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(svgString, "text/xml")

  // Find the elements to change (e.g., all 'path' elements)
  const paths = xmlDoc.getElementsByTagName("path")
  for (let i = 0; i < paths.length; i++) {
    paths[i].setAttribute("fill", newColor) // Change the fill color
  }

  // Serialize the modified SVG back to string
  const serializer = new XMLSerializer()
  const modifiedSvgString = serializer.serializeToString(xmlDoc)
  return modifiedSvgString
}

export async function resizeImage(dataUrl: string, maxWidth: number, maxHeight: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      let { width, height } = img

      // Calculate the new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }
      }

      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height)
        const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.7) // Adjust quality if needed
        resolve(resizedDataUrl)
      } else {
        throw new Error("Unable to get 2D context from canvas")
      }
    }
    img.src = dataUrl
  })
}

export const toBase64 = async (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
  })

export const reduceImageQuality = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target?.result as string
      img.onload = () => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          reject(new Error("Failed to get canvas context"))
          return
        }

        // Calculate dimensions while maintaining aspect ratio
        const maxDimension = 2200
        const scaleFactor = Math.min(1, maxDimension / Math.max(img.width, img.height))
        canvas.width = img.width * scaleFactor
        canvas.height = img.height * scaleFactor

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

        const reduceQuality = (quality: number = 0.95): string => {
          const dataUrl = canvas.toDataURL("image/jpeg", quality)
          const binaryString = atob(dataUrl.split(",")[1])
          const imageSizeInBytes = binaryString.length

          if (imageSizeInBytes > MAX_IMAGE_SIZE && quality > 0.1) {
            return reduceQuality(quality - 0.05)
          }

          return dataUrl
        }

        resolve(reduceQuality())
      }
      img.onerror = () => reject(new Error("Failed to load image"))
    }
    reader.onerror = () => reject(new Error("Failed to read file"))
  })
}

// Helper function to extract EXIF data using ExifReader
export const getExifData = async (
  file: File,
): Promise<{
  latitude?: number
  longitude?: number
  orientation?: number
}> => {
  // Default empty result
  const result: {
    latitude?: number
    longitude?: number
    orientation?: number
  } = {}

  // Only process image files
  if (!file.type.startsWith("image/")) {
    return result
  }

  try {
    // Read file as ArrayBuffer
    const buffer = await file.arrayBuffer()

    // Parse EXIF data
    const tags = ExifReader.load(buffer, { expanded: true })

    // Extract GPS data if available
    if (tags.gps?.Latitude && tags.gps?.Longitude) {
      result.latitude = tags.gps?.Latitude
      result.longitude = tags.gps?.Longitude
    }

    // Extract orientation
    if (tags.exif?.Orientation) {
      const orientationValue = tags.exif?.Orientation.value

      // Convert EXIF orientation value to degrees
      // Note: This mapping might need adjustment based on your specific needs
      let orientationDegrees = 0
      switch (orientationValue) {
        case 1: // Normal
          orientationDegrees = 0
          break
        case 3: // 180° rotate left
          orientationDegrees = 180
          break
        case 6: // 90° rotate right
          orientationDegrees = 90
          break
        case 8: // 90° rotate left
          orientationDegrees = 270
          break
      }

      result.orientation = orientationDegrees
    }

    if (tags.exif?.GPSDestBearing) {
      if (tags.exif?.GPSDestBearing.description !== undefined) {
        // If description is available, use it
        // Note: The description might be a string representation of the value
        const gpsDestBearing = tags.exif?.GPSDestBearing.description
        result.orientation = parseFloat(gpsDestBearing)
      } else if (tags.exif?.GPSDestBearing.value !== undefined) {
        const gpsDestBearing = tags.exif?.GPSDestBearing.value
        const gpsDestBearingRef = tags.exif?.GPSDestBearingRef?.value

        // Convert bearing to degrees
        if (gpsDestBearing && gpsDestBearingRef) {
          const direction = gpsDestBearingRef[0] === "T" ? 1 : -1 // True or Magnetic
          result.orientation = (gpsDestBearing * direction) % 360
        }
      }
    }

    // If GPS data isn't available directly, try alternative tags
    // if (result.latitude === undefined && result.longitude === undefined) {
    //   // Some images store GPS in different formats or with different tags
    //   if (tags.GPSInfo && tags.GPSInfo.value) {
    //     const gpsInfo = tags.GPSInfo.value
    //     if (gpsInfo.latitude && gpsInfo.longitude) {
    //       result.latitude = gpsInfo.latitude
    //       result.longitude = gpsInfo.longitude
    //     }
    //   }
    // }

    console.log("EXIF data extracted:", result)
    return result
  } catch (error) {
    console.error("Error extracting EXIF data:", error)
    return result
  }
}

// Helper function to convert GPS coordinates from DMS format to decimal degrees
const convertDMSToDD = (degrees: number, minutes: number, seconds: number, direction: number): number => {
  const dd = degrees + minutes / 60 + seconds / 3600
  return dd * direction
}
